import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from './baseQuery';

export interface ProposalItem {
  id: string;
  postId?: string;
  postTitle?: string;
  profile?: {
    id: string;
    name?: string;
    photoUrl?: string;
    rating?: number | string | null;
    reviewsCount?: number | string | null;
    ordersCount?: number | string | null;
    avgResponse?: string | null;
  };
  coverLetter?: string;
  submittedAt?: string | Date;
  timeUnit?: {
    id?: string;
    code?: string;
    name?: string;
    nameAr?: string;
    nameEn?: string;
  };
  totalPrice?: number | string | null;
  totalPriceConverted?: number | string | null;
  deliveryTime?: number | string | null;
  totalAttachments?: number | string | null;
  isShortlisted?: boolean;
  oldTotalPrice?: number | string | null;
  oldTotalPriceConverted?: number | string | null;
  oldDeliveryTime?: number | string | null;
  loading?: boolean;
  statuses?: { name: string; code: string }[];
  isEvaluated?: boolean;
  totalScore?: number | string | null;
  actions?: {
    canViewDetails?: boolean;
    canMakeContract?: boolean;
    canChat?: boolean;
    canEvaluate?: boolean;
    canNegotiate?: boolean;
    canMakeShortlist?: boolean;
  };
  proposalHasOpenChat?: boolean;
  hasChatBefore?: boolean;
  requestType?: unknown[];
}

export interface ProposalsListResponse {
  payload: {
    content: ProposalItem[];
    totalElements: number;
  };
}

export interface ProposalSortLookupItem {
  code: string;
  id: string;
  name: string;
  sort?: number;
  nameAr?: string;
  nameEn?: string;
}

export interface ProposalSortLookupResponse {
  payload: ProposalSortLookupItem[];
}

export const biddingProposalsService = createApi({
  reducerPath: 'biddingProposalsService',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['BiddingProposals', 'ProposalSort'],
  endpoints: (builder) => ({
    getProposalsList: builder.query<
      ProposalsListResponse,
      { postId: string; page?: string | number; sort?: string }
    >({
      query: ({ postId, page = 0, sort }) => ({
        url: `/aggregation-service/v1/operator/posts/${postId}/proposals`,
        method: 'GET',
        params: {
          page: Number(page) > 0 ? Number(page) - 1 : 0,
          ...(sort ? { sort } : {}),
        },
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.payload.content.map(({ id }) => ({
                type: 'BiddingProposals' as const,
                id,
              })),
              { type: 'BiddingProposals', id: 'LIST' },
            ]
          : [{ type: 'BiddingProposals', id: 'LIST' }],
    }),
    getProposalSortLookup: builder.query<ProposalSortLookupResponse, void>({
      query: () => ({
        url: '/lookup-service/v1/operator/proposals-sort',
        method: 'GET',
      }),
      providesTags: [{ type: 'ProposalSort', id: 'LIST' }],
    }),
    getProposalsCount: builder.query<{ payload: { count: number } | number }, { postId: string }>({
      query: ({ postId }) => ({
        url: `/supply-service/v1/operator/proposals/count/${postId}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, { postId }) => [
        { type: 'BiddingProposals', id: `COUNT_${postId}` },
      ],
    }),
  }),
});

export const {
  useGetProposalsListQuery,
  useGetProposalSortLookupQuery,
  useGetProposalsCountQuery,
} = biddingProposalsService;
